// src/pages/bank-details/BankDetailsForm.tsx
import SelectDropdown from '@/components/common/SelectDropdown';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { bankOptions } from '@/lib/constants/bank-details';
import { bankDetailsSchema } from '@/lib/schemas/bank-details.schema';
import { bankDetailsAPI } from '@/lib/utils/api';
import { BankDetailsFormData } from '@/types/bank-details.types';
import { Form, Formik, FormikHelpers } from 'formik';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const initialValues: BankDetailsFormData = {
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  upiId: '',
  isDefault: false,
};

const BankDetailsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [formData, setFormData] = useState<BankDetailsFormData>(initialValues);

  // Fetch Bank Details (Edit)
  useEffect(() => {
    if (!isEditing) return;

    const fetchBankDetails = async () => {
      try {
        const res = await bankDetailsAPI.getById(id as string);
        const bankDetails = res.data;

        setFormData({
          ...initialValues,
          ...bankDetails,
        });
      } catch {
        toast.error('Failed to fetch bank details');
        navigate('/bank-details');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchBankDetails();
  }, [id, isEditing, navigate]);

  // Submit
  const handleSubmit = async (
    values: BankDetailsFormData,
    { setSubmitting }: FormikHelpers<BankDetailsFormData>
  ) => {
    setLoading(true);

    try {
      const payload = {
        ...values,
        ifscCode: values.ifscCode.toUpperCase(),
      };

      isEditing
        ? await bankDetailsAPI.update(id as string, payload)
        : await bankDetailsAPI.create(payload);

      toast.success(`Bank account ${isEditing ? 'updated' : 'created'} successfully`);
      navigate('/bank-details');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate('/bank-details')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Edit Bank Account' : 'Add Bank Account'}
        </h1>
      </div>

      {/* Form */}
      <div className="bg-background rounded-lg shadow p-6">
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={bankDetailsSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form className="space-y-6">
              {/* ================= BANK INFO ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">Bank Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bank Name */}
                  <div className="space-y-2">
                    <Label>Bank Name *</Label>
                    <div className="space-y-1">
                      <SelectDropdown
                        value={values.bankName}
                        options={bankOptions}
                        placeholder="Select a bank"
                        onChange={(val) => {
                          setFieldValue('bankName', val);
                        }}
                      />
                      {touched.bankName && errors.bankName && (
                        <p className="text-sm text-red-500">{errors.bankName}</p>
                      )}
                    </div>
                  </div>

                  {/* Account Holder Name */}
                  <div className="space-y-2">
                    <Label>Account Holder Name *</Label>
                    <div className="space-y-1">
                      <Input
                        name="accountHolderName"
                        value={values.accountHolderName}
                        placeholder="Enter account holder name"
                        onChange={handleChange}
                      />
                      {touched.accountHolderName && errors.accountHolderName && (
                        <p className="text-sm text-red-500">{errors.accountHolderName}</p>
                      )}
                    </div>
                  </div>

                  {/* Account Number */}
                  <div className="space-y-2">
                    <Label>Account Number *</Label>
                    <div className="space-y-1">
                      <Input
                        name="accountNumber"
                        value={values.accountNumber}
                        placeholder="Enter account number"
                        onChange={handleChange}
                      />
                      {touched.accountNumber && errors.accountNumber && (
                        <p className="text-sm text-red-500">{errors.accountNumber}</p>
                      )}
                    </div>
                  </div>

                  {/* IFSC Code */}
                  <div className="space-y-2">
                    <Label>IFSC Code *</Label>
                    <div className="space-y-1">
                      <Input
                        name="ifscCode"
                        value={values.ifscCode}
                        placeholder="e.g., SBIN0001234"
                        onChange={(e) => {
                          const upperValue = e.target.value.toUpperCase();
                          setFieldValue('ifscCode', upperValue);
                        }}
                      />
                      {touched.ifscCode && errors.ifscCode && (
                        <p className="text-sm text-red-500">{errors.ifscCode}</p>
                      )}
                    </div>
                  </div>

                  {/* UPI ID */}
                  <div className="space-y-2">
                    <Label>UPI ID</Label>
                    <div className="space-y-1">
                      <Input
                        name="upiId"
                        value={values.upiId}
                        placeholder="e.g., yourname@upi"
                        onChange={handleChange}
                      />
                      {touched.upiId && errors.upiId && (
                        <p className="text-sm text-red-500">{errors.upiId}</p>
                      )}
                    </div>
                  </div>

                  {/* Is Default */}
                  <div className="flex items-center space-x-2 pt-6">
                    <Checkbox
                      id="isDefault"
                      checked={values.isDefault}
                      onCheckedChange={(checked) => {
                        setFieldValue('isDefault', checked);
                      }}
                    />
                    <Label htmlFor="isDefault" className="cursor-pointer">
                      Set as default bank account
                    </Label>
                  </div>
                </div>
              </div>

              {/* ================= ACTIONS ================= */}
              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => navigate('/bank-details')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading
                    ? 'Saving...'
                    : isEditing
                      ? 'Update Bank Account'
                      : 'Create Bank Account'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default BankDetailsForm;
